import config from '@app/config';
import { removeIrrelevantAccountDetailsFields } from '@app/domain/accountsDetailsHelpers';
import request, { HttpMethod, HttpStatus, RequestError } from '@app/libs/request';
import {
  defaultMetricsResult,
  MatchingValidationRecordServerResponse,
  morteeTransformSearchPayeeAccount,
  PayeeMetricsResults,
  SearchServerResponse,
  SearchStatusType,
  SergeeSearchResult,
  SergeeSearchScore,
  transformMetricsResult,
} from '@mortee/domain/morteeAccountVerification';
import { UnifiedPayeeIds } from '@mortee/domain/morteeAccountVerificationUnifiedPayee';

export async function searchPayeeAccount(
  payee: { id?: string; ids?: UnifiedPayeeIds; name: string; taxId?: string },
  accountDetails: Partial<MorteeAccountDetailsExtended>,
): Promise<SergeeSearchResult> {
  const data = {
    payee: { name: payee.name, ...(payee.taxId ? { taxId: payee.taxId } : { ...payee.ids }), validatedId: payee.id },
    account: removeIrrelevantAccountDetailsFields(accountDetails),
  };
  try {
    const response = await request<SearchServerResponse>(config.serverUrls['sergee'], `/api/search`, {
      method: HttpMethod.post,
      data,
      errorsHandler: {
        default: {
          message: 'Something went wrong while verifying the payee account.',
        },
      },
    });

    return morteeTransformSearchPayeeAccount(response);
  } catch (e: unknown) {
    const requestError = e as RequestError;

    if (requestError.code === HttpStatus.notFound) {
      return {
        createTimestamp: Date.now(),
        status: SearchStatusType.Error,
        id: '',
        searchRequest: {
          payee: { name: payee.name, ids: payee.ids || {}, validatedPayeeUniformId: payee.id || '' },
          account: removeIrrelevantAccountDetailsFields(accountDetails),
        },
        score: SergeeSearchScore.NotValidated,
        metricsResult: transformMetricsResult(defaultMetricsResult as PayeeMetricsResults),
        quickCheckId: '',
        validatedPayeeUniformId: '',
      };
    }

    throw e;
  }
}

export async function saveQuickCheckValidation(searchId: string): Promise<string | null> {
  try {
    const response = await request<SearchServerResponse>(
      config.serverUrls['sergee'],
      `/api/search/${searchId}/validate-quick-check`,
      {
        method: HttpMethod.post,
        errorsHandler: {
          default: {
            message: 'Something went wrong while saving the quick check validation.',
          },
        },
      },
    );

    return response.id;
  } catch (e: unknown) {
    const requestError = e as RequestError;

    if (requestError.code === HttpStatus.notFound) {
      return null;
    }

    throw e;
  }
}

export async function getMatchingValidationRecords(searchId: string): Promise<MatchingValidationRecordServerResponse[] | null> {
  try {
    return await request<MatchingValidationRecordServerResponse[]>(
      config.serverUrls['sergee'],
      `/api/search/${searchId}/matching-validation-records`,
      {
        method: HttpMethod.get,
        errorsHandler: {
          default: {
            message: 'Something went wrong while getting the matching validation records.',
          },
        },
      },
    );
  } catch (e: unknown) {
    const requestError = e as RequestError;

    if (requestError.code === HttpStatus.notFound) {
      return null;
    }

    throw e;
  }
}

export async function cancelValidationRecords(searchId: string, validationRecordStaticId: string): Promise<string | null> {
  try {
    await request<any>(
      config.serverUrls['sergee'],
      `/api/search/${searchId}/matching-validation-records/${validationRecordStaticId}/cancel`,
      {
        method: HttpMethod.patch,
        errorsHandler: {
          default: {
            message: 'Something went wrong while canceling the validation record.',
          },
        },
      },
    );

    return 'OK';
  } catch (e: unknown) {
    return null;
  }
}
